// R109 #d (Jesse: "Add a smart search feature on the WOs, so you type in things
// and the system tries to find the WO"). Pure scoring for the Maintenance →
// Work orders search box — no DOM, no I/O. ESM (not the CommonJS of the other
// lib/ modules) so the Vite client imports it directly from MaintenancePage.ts.
// Tested by `node lib/woSearch.test.mjs`.
//
// How a query is read:
//   "quoted words"        must appear together, in that order
//   -word                 leave out work orders that mention it
//   dates                 today · yesterday · tomorrow · this/last/next week ·
//                         this/last/next month · this/last year · last 10 days ·
//                         june · jun 5 · june 2025 · 6/5 · 2026-06-05
//                         optionally prefixed with done / created / due / booked
//                         ("done last week", "created: june")
//   status words          open · done · urgent · emergency · overdue ·
//                         unassigned · unbilled … match the WO's state only,
//                         never a description that happens to say "done"
//   everything else       words matched against id, title, property, assignee,
//                         vendor, asset, checklist, instructions, notes,
//                         comments and history — whole word, start of a word
//                         or inside a word, with typo tolerance for words that
//                         appear nowhere ("kitchn" → kitchen)
// Every word must match (AND). When nothing matches every word, the closest
// work orders (at least half the words) come back flagged loose.

const MONTHS = ['january', 'february', 'march', 'april', 'may', 'june', 'july', 'august', 'september', 'october', 'november', 'december'];
const MONTH_RE = 'jan(?:uary)?|feb(?:ruary)?|mar(?:ch)?|apr(?:il)?|may|june?|july?|aug(?:ust)?|sep(?:t(?:ember)?)?|oct(?:ober)?|nov(?:ember)?|dec(?:ember)?';
const PREFIX_RE = '(?:(done|completed|complete|finished|closed|fixed|created|opened|logged|reported|due|booked|scheduled)(?::\\s*|\\s+))?';
const PREFIX_TARGET = {
  done: 'done', completed: 'done', complete: 'done', finished: 'done', closed: 'done', fixed: 'done',
  created: 'created', opened: 'created', logged: 'created', reported: 'created',
  due: 'due', booked: 'sched', scheduled: 'sched',
};
// Words that carry no meaning in a work-order search ("kitchen light at 740").
const STOPWORDS = new Set(['a', 'an', 'the', 'at', 'in', 'on', 'of', 'for', 'and', 'or', 'to', 'with', 'by', 'is', 'was', 'it', 'its', 'my', 'our', 'no', 'wo', 'wos', 'please']);
// Status / state words: matched against the synthetic tags field only.
export const FACETS = new Set(['open', 'active', 'done', 'completed', 'complete', 'closed', 'finished', 'wip', 'emergency', 'urgent', 'routine', 'pm', 'preventive',
  'unassigned', 'billed', 'unbilled', 'overdue', 'unscheduled', 'booked', 'scheduled', 'followup']);
// Fields not shown on a collapsed card — a hit there gets a "found in …" line.
export const HIDDEN_FIELDS = new Set(['id', 'description', 'checklist', 'checklist note', 'completion', 'follow-up', 'summary', 'vendor report', 'comment', 'invoice', 'history']);
export const FIELD_LABEL = {
  id: 'work order id', description: 'instructions', checklist: 'checklist', 'checklist note': 'checklist note', completion: 'what was done',
  'follow-up': 'follow-up', summary: 'owner summary', 'vendor report': 'vendor report', comment: 'comment', invoice: 'invoice', history: 'history',
};

const ACCENTS = /[̀-ͯ]/g;
// Text → lowercase words separated by single spaces. Apostrophes join
// ("Allan's" → allans), "A/C" and "W/D" stay one word, and every other
// non-letter/digit splits — so "working." still matches the word "working".
// '_' is kept so an id like wo_lx2k9a stays one word.
export function norm(s) {
  return String(s == null ? '' : s).normalize('NFD').replace(ACCENTS, '').toLowerCase()
    .replace(/['’]/g, '')
    .replace(/\b([a-z])\/([a-z])\b/g, '$1$2')
    .replace(/[^a-z0-9_]+/g, ' ')
    .trim();
}

// ── dates ──────────────────────────────────────────────────────────────
const pad2 = (n) => String(n).padStart(2, '0');
const ymd = (d) => `${d.getUTCFullYear()}-${pad2(d.getUTCMonth() + 1)}-${pad2(d.getUTCDate())}`;
const dayObj = (s) => new Date(`${s}T00:00:00Z`);
const addDays = (s, n) => { const d = dayObj(s); d.setUTCDate(d.getUTCDate() + n); return ymd(d); };
const addMonths = (s, n) => { const d = dayObj(s); const day = d.getUTCDate(); d.setUTCDate(1); d.setUTCMonth(d.getUTCMonth() + n); const last = new Date(Date.UTC(d.getUTCFullYear(), d.getUTCMonth() + 1, 0)).getUTCDate(); d.setUTCDate(Math.min(day, last)); return ymd(d); };
const monthEnd = (y, mi) => `${y}-${pad2(mi + 1)}-${pad2(new Date(Date.UTC(y, mi + 1, 0)).getUTCDate())}`;
const validDay = (s) => /^\d{4}-\d{2}-\d{2}$/.test(s) && ymd(dayObj(s)) === s;
const fmtShort = (s) => { const d = dayObj(s); return `${MONTHS[d.getUTCMonth()].slice(0, 3).replace(/^./, c => c.toUpperCase())} ${d.getUTCDate()}`; };
const rangeLabel = (from, to) => from === to ? fmtShort(from) : `${fmtShort(from)} – ${fmtShort(to)}${from.slice(0, 4) !== to.slice(0, 4) ? ` ${to.slice(0, 4)}` : ''}`;

// Device-local calendar day of a timestamp (the R50/R39 rule: an evening-PT
// completion belongs to the day the team saw it, not the next UTC day). A bare
// YYYY-MM-DD is already a day.
export function localDay(iso) {
  if (!iso) return '';
  const s = String(iso);
  if (/^\d{4}-\d{2}-\d{2}$/.test(s)) return s;
  const d = new Date(s);
  return isNaN(d.getTime()) ? '' : d.toLocaleDateString('en-CA');
}

// Pull the date phrases out of a lowercased query.
// → { ranges: [{from, to, label, target: any|done|created|due|sched}], rest }
export function parseDates(q, today) {
  const ranges = [];
  let rest = ` ${String(q).replace(/[,;]+/g, ' ')} `;
  const tgt = (p) => (p ? PREFIX_TARGET[p] : 'any');
  const forward = (t) => t === 'due' || t === 'sched';   // a due / booked date → the NEXT occurrence; anything else → the most recent
  // named ranges ("last week") show their days; a single typed day is its own label
  const push = (from, to, label, target, plain) => {
    if (!(validDay(from) && validDay(to) && from <= to)) return false;
    ranges.push({ from, to, label: plain ? label : `${label} (${rangeLabel(from, to)})`, target });
    return true;
  };
  // Leading whitespace is consumed, trailing is a lookahead — back-to-back
  // phrases ("june july") both parse. No lookbehind: older iOS Safari lacks it.
  const take = (body, fn) => {
    const re = new RegExp(`(^|\\s)${PREFIX_RE}(?:${body})(?=\\s|$)`, 'g');
    rest = rest.replace(re, (...m) => (fn(m.slice(2, -2)) ? ' ' : m[0]));
  };
  const dow = dayObj(today).getUTCDay();
  const monStart = addDays(today, -((dow + 6) % 7));
  take('today', ([p]) => push(today, today, 'today', tgt(p)));
  take('yesterday', ([p]) => push(addDays(today, -1), addDays(today, -1), 'yesterday', tgt(p)));
  take('tomorrow', ([p]) => push(addDays(today, 1), addDays(today, 1), 'tomorrow', tgt(p)));
  take('this week', ([p]) => push(monStart, addDays(monStart, 6), 'this week', tgt(p)));
  take('last week', ([p]) => push(addDays(monStart, -7), addDays(monStart, -1), 'last week', tgt(p)));
  take('next week', ([p]) => push(addDays(monStart, 7), addDays(monStart, 13), 'next week', tgt(p)));
  const y0 = Number(today.slice(0, 4)), m0 = Number(today.slice(5, 7)) - 1;
  take('this month', ([p]) => push(`${today.slice(0, 7)}-01`, monthEnd(y0, m0), 'this month', tgt(p)));
  take('last month', ([p]) => { const s = addMonths(`${today.slice(0, 7)}-01`, -1); return push(s, monthEnd(Number(s.slice(0, 4)), Number(s.slice(5, 7)) - 1), 'last month', tgt(p)); });
  take('next month', ([p]) => { const s = addMonths(`${today.slice(0, 7)}-01`, 1); return push(s, monthEnd(Number(s.slice(0, 4)), Number(s.slice(5, 7)) - 1), 'next month', tgt(p)); });
  take('this year', ([p]) => push(`${y0}-01-01`, `${y0}-12-31`, 'this year', tgt(p)));
  take('last year', ([p]) => push(`${y0 - 1}-01-01`, `${y0 - 1}-12-31`, 'last year', tgt(p)));
  // rolling windows — after the calendar phrases above ("last month" = September, "past month" = 30-ish days)
  take('(?:last|past) (?:(\\d{1,3}) ?)?(days?|d|weeks?|wks?|w|months?|mos?)', ([p, n, u]) => {
    const k = n == null ? 1 : Number(n); if (!k) return false;
    const from = /^d/.test(u) ? addDays(today, -k) : /^w/.test(u) ? addDays(today, -7 * k) : addMonths(today, -k);
    const unit = /^d/.test(u) ? 'day' : /^w/.test(u) ? 'week' : 'month';
    return push(from, today, n == null ? `past ${unit}` : `last ${k} ${unit}${k === 1 ? '' : 's'}`, tgt(p));
  });
  take('(\\d{4})-(\\d{1,2})-(\\d{1,2})', ([p, y, mo, d]) => { const s = `${y}-${pad2(mo)}-${pad2(d)}`; return validDay(s) && push(s, s, `${fmtShort(s)} ${y}`, tgt(p), true); });
  // No year typed: a due / booked date is the NEAREST occurrence; anything else
  // the most recent one that has started ("june" in January = last June).
  const dist = (s) => Math.abs(dayObj(s).getTime() - dayObj(today).getTime());
  const pickYear = (mk, t) => {
    const c = [y0 - 1, y0, y0 + 1].map(mk).filter(validDay);
    if (!c.length) return null;
    if (forward(t)) return [...c].sort((a, b) => dist(a) - dist(b))[0];
    const started = c.filter(s => s <= today);
    return started.length ? started[started.length - 1] : c[0];
  };
  take('(\\d{1,2})/(\\d{1,2})(?:/(\\d{4}|\\d{2}))?', ([p, mo, d, yy]) => {
    const t = tgt(p);
    const s = yy ? `${yy.length === 2 ? `20${yy}` : yy}-${pad2(mo)}-${pad2(d)}` : pickYear(y => `${y}-${pad2(mo)}-${pad2(d)}`, t);
    return !!s && validDay(s) && push(s, s, `${fmtShort(s)} ${s.slice(0, 4)}`, t, true);
  });
  take(`(${MONTH_RE})(?: (\\d{1,2})(?:st|nd|rd|th)?)?(?: (\\d{4}))?`, ([p, mon, d, yr]) => {
    const t = tgt(p);
    const mm = pad2(MONTHS.findIndex(x => x.startsWith(mon.slice(0, 3))) + 1);
    if (d) {
      const s = yr ? `${yr}-${mm}-${pad2(d)}` : pickYear(y => `${y}-${mm}-${pad2(d)}`, t);
      return !!s && validDay(s) && push(s, s, `${fmtShort(s)} ${s.slice(0, 4)}`, t, true);
    }
    const s = yr ? `${yr}-${mm}-01` : pickYear(y => `${y}-${mm}-01`, t);
    return !!s && push(s, monthEnd(Number(s.slice(0, 4)), Number(mm) - 1), `${MONTHS[Number(mm) - 1]} ${s.slice(0, 4)}`, t);
  });
  return { ranges, rest: rest.replace(/\s+/g, ' ').trim() };
}

// ── query ──────────────────────────────────────────────────────────────
// → { terms: [{t, facet}], phrases: [string], neg: [{t, facet, phrase?}], ranges, active }
export function parseQuery(q, today) {
  const phrases = [];
  const s0 = String(q == null ? '' : q).replace(/[“”]/g, '"');
  const noPhrases = s0.replace(/"([^"]*)"?/g, (_, p) => { const n = norm(p); if (n) phrases.push(n); return ' '; });
  const lowered = noPhrases.normalize('NFD').replace(ACCENTS, '').toLowerCase();
  const { ranges, rest } = parseDates(lowered, today);
  const neg = [], words = [];
  for (const tok of rest.split(' ').filter(Boolean)) {
    // "-leak" excludes a word; "-touch-up" excludes the PHRASE "touch up" (not
    // every WO with "up" in it); stopwords and lone letters never exclude
    if (tok.length > 1 && tok[0] === '-' && /[a-z0-9]/.test(tok[1])) {
      const ws = norm(tok.slice(1)).split(' ').filter(Boolean);
      if (ws.length > 1) neg.push({ t: ws.join(' '), facet: false, phrase: true });
      else if (ws[0] && !STOPWORDS.has(ws[0]) && (ws[0].length >= 2 || /^\d$/.test(ws[0]))) neg.push({ t: ws[0], facet: FACETS.has(ws[0]) });
    }
    else for (const w of norm(tok).split(' ').filter(Boolean)) words.push(w);
  }
  const kept = words.filter(w => !STOPWORDS.has(w));
  // a lone letter is the first keystroke of a word, not a search ("k" → wait for "ki"); digits count ("unit 2")
  const uniq = [...new Set(kept.length ? kept : words)].filter(w => w.length > 1 || /\d/.test(w));
  const terms = uniq.map(t => ({ t, facet: FACETS.has(t) }));
  return { terms, phrases, neg, ranges, active: !!(terms.length || phrases.length || neg.length || ranges.length) };
}

// ── documents ──────────────────────────────────────────────────────────
// One searchable doc per work order. ctx: { today, vendorName(id), assetName(id),
// schedOf(w) → YYYY-MM-DD|null, doneAt(w) → iso|null, localDay?(iso) }.
export function buildDoc(w, ctx) {
  const c = ctx || {};
  const day = c.localDay || localDay;
  const f = [];
  const add = (key, weight, text, extra) => {
    const raw = String(text == null ? '' : text).trim();
    if (!raw) return;
    const n = norm(raw) + (extra ? ` ${norm(extra)}` : '');
    if (n.trim()) f.push({ key, weight, raw, n, sp: ` ${n} ` });
  };
  const id = String(w.id || '');
  add('id', 6, id, id.replace(/^[a-z]+_/i, ''));   // "lx2k9a" finds wo_lx2k9a
  add('title', 5, w.title);
  add('property', 4, w.listing_name);
  add('assignee', 3, w.assigned_to);
  add('vendor', 3, c.vendorName ? c.vendorName(w.vendor_id) : '');
  add('asset', 2.5, c.assetName ? c.assetName(w.asset_id) : '');
  for (const it of (Array.isArray(w.items) ? w.items : [])) { if (!it) continue; add('checklist', 2.5, it.title); add('checklist note', 1.5, it.note); }
  add('description', 2, w.description);
  add('completion', 2, w.completion && w.completion.what_done);
  add('summary', 2, w.summary);
  add('submitted by', 1.5, w.created_by);
  add('follow-up', 1.5, w.completion && w.completion.followup_note);
  add('vendor report', 1.5, w.vendor_report && (w.vendor_report.work_done || w.vendor_report.result));
  for (const x of [...(Array.isArray(w.comments) ? w.comments : []), ...(Array.isArray(w.internal_notes) ? w.internal_notes : [])]) add('comment', 1.2, x && x.text);
  for (const inv of (Array.isArray(w.invoices) ? w.invoices : [])) add('invoice', 1, inv && inv.name);
  for (const l of (Array.isArray(w.log) ? w.log : []).slice(-15)) add('history', 0.8, l && l.note);

  const doneIso = c.doneAt ? c.doneAt(w) : (w.status === 'done' ? w.completed_at : null);
  const done = !!doneIso || w.status === 'done';
  const sched = c.schedOf ? c.schedOf(w) : (w.scheduled_date || null);
  const due = w.effective_due || w.due_date || null;
  const dueDay = due ? day(due) : '';
  const today = c.today || '';
  const prio = w.priority || (w.type === 'pm' ? 'pm' : 'routine');
  const tags = [
    w.status === 'in_progress' ? 'open active in progress wip started' : (done ? 'done completed complete closed finished' : 'open active'),
    prio === 'emergency' ? 'emergency urgent' : prio,
    w.type === 'pm' ? 'pm preventive' : '',
    w.role || 'maintenance',
    (!w.assigned_to && !w.vendor_id) ? 'unassigned' : '',
    w.billing_status ? 'billed' : 'unbilled',
    w.payer === 'owner' ? 'owner' : (w.payer === 'wch' ? 'wch' : ''),
    sched ? 'booked scheduled' : (done ? '' : 'unscheduled'),
    (!done && today && ((sched && sched < today) || (dueDay && dueDay < today))) ? 'overdue' : '',
    (!done && w.completion && w.completion.needs_followup) ? 'followup follow up' : '',
    w.claim_tracked ? 'claim' : '',
  ].filter(Boolean).join(' ');
  add('tags', 1.5, tags);
  return {
    w, f,
    tags: f.find(x => x.key === 'tags') || null,
    all: ` ${f.map(x => x.n).join(' | ')} `,
    dates: { created: day(w.created_at), done: doneIso ? day(doneIso) : '', due: dueDay, sched: sched || '' },
    recent: Math.max(Date.parse(w.created_at) || 0, Date.parse(doneIso) || 0),
  };
}

// ── typo tolerance ─────────────────────────────────────────────────────
// Optimal-string-alignment distance (a swapped pair of letters costs 1) from
// `a` to the closest PREFIX of `b` — "kitchn" is 1 from "kitchenette".
function prefixDistance(a, b, max) {
  if (b.length < a.length - max) return max + 1;
  const bb = b.slice(0, a.length + max);
  const rows = [Array.from({ length: bb.length + 1 }, (_, j) => j)];
  for (let i = 1; i <= a.length; i++) {
    const cur = [i]; let rowMin = i;
    for (let j = 1; j <= bb.length; j++) {
      const cost = a[i - 1] === bb[j - 1] ? 0 : 1;
      let v = Math.min(rows[i - 1][j] + 1, cur[j - 1] + 1, rows[i - 1][j - 1] + cost);
      if (i > 1 && j > 1 && a[i - 1] === bb[j - 2] && a[i - 2] === bb[j - 1]) v = Math.min(v, rows[i - 2][j - 2] + 1);
      cur[j] = v; if (v < rowMin) rowMin = v;
    }
    if (rowMin > max) return max + 1;
    rows.push(cur);
  }
  const last = rows[a.length];
  let best = max + 1;
  for (let j = Math.max(1, a.length - max); j <= bb.length; j++) if (last[j] < best) best = last[j];
  return best;
}
// Words close to `t` across the whole set — only asked for a word that
// appears NOWHERE, so a correctly spelled "pool" never drags in "tool".
function fuzzyAlts(t, vocab) {
  const max = t.length >= 8 ? 2 : 1;
  const out = [];
  for (const v of vocab) {
    if (v.length < 3 || /^\d+$/.test(v)) continue;
    const d = prefixDistance(t, v, max);
    if (d <= max) out.push([v, d]);
  }
  return out.sort((a, b) => a[1] - b[1] || a[0].length - b[0].length).slice(0, 12).map(x => x[0]);
}

// ── scoring ────────────────────────────────────────────────────────────
// whole word 1 · start of a word 0.85 · inside a word 0.55. Two-letter words
// and the synthetic tags only count as whole words ("ac" is not "active").
function matchQuality(x, t) {
  if (x.sp.includes(` ${t} `)) return 1;
  if (x.key === 'tags' || t.length < 3) return 0;
  if (x.sp.includes(` ${t}`)) return 0.85;
  if (x.n.includes(t)) return 0.55;
  return 0;
}
function scoreDoc(doc, pq, alts, loose) {
  for (const r of pq.ranges) {
    const ds = r.target === 'any' ? [doc.dates.created, doc.dates.done, doc.dates.due, doc.dates.sched] : [doc.dates[r.target]];
    if (!ds.some(d => d && d >= r.from && d <= r.to)) return null;
  }
  const tags = doc.tags;
  // exclusions follow the same word rules as matches: "-ac" drops the A/C
  // jobs, not every WO whose hidden tags say "active" or whose text says "replace"
  for (const n of pq.neg) {
    // one rule: "-x" drops exactly what "x" matches as a whole word / word start
    // (tags included — matchQuality only takes whole words there)
    if (n.facet ? (tags && tags.sp.includes(` ${n.t} `)) : n.phrase ? doc.f.some(x => x.sp.includes(` ${n.t} `)) : doc.f.some(x => matchQuality(x, n.t) >= 0.85)) return null;
  }
  let score = 0, missed = 0;
  const hits = [];
  for (const p of pq.phrases) {
    let best = 0, bf = null;
    for (const x of doc.f) { const q = matchQuality(x, p); if (q && q * x.weight > best) { best = q * x.weight; bf = x; } }
    if (!bf) return null;   // a quoted phrase is never optional
    score += best * 1.2; hits.push({ term: p, field: bf.key, raw: bf.raw });
  }
  for (const { t, facet } of pq.terms) {
    let best = 0, bf = null;
    if (facet) {
      if (tags && tags.sp.includes(` ${t} `)) { best = tags.weight; bf = tags; }
    } else {
      for (const x of doc.f) { const q = matchQuality(x, t); if (q && q * x.weight > best) { best = q * x.weight; bf = x; } }
      const near = alts.get(t);
      if (!bf && near && near.length) {
        for (const x of doc.f) { if (near.some(a => x.sp.includes(` ${a} `)) && 0.5 * x.weight > best) { best = 0.5 * x.weight; bf = x; } }
      }
    }
    if (!bf) { if (loose) { missed++; continue; } return null; }
    score += best; hits.push({ term: t, field: bf.key, raw: bf.raw });
  }
  if (loose && (missed === pq.terms.length || missed > Math.floor(pq.terms.length / 2))) return null;   // closest = at least half the words
  // the words in the order typed, together — "kitchen light" beats kitchen … light
  if (pq.terms.length > 1) {
    const joined = pq.terms.map(x => x.t).join(' ');
    const x = doc.f.find(y => y.key !== 'tags' && y.sp.includes(` ${joined}`));
    if (x) score += x.weight * 0.5;
  }
  return { score: score - missed * 2, hits, missed };
}

// docs → ranked rows. now = Date.now() override for tests.
// → { rows: [{w, score, hits, loose?}], loose, hlTerms, fuzzy: {term: [alts]} }
export function searchDocs(docs, pq, now) {
  const tNow = now == null ? Date.now() : now;
  const alts = new Map();
  let vocab = null;
  for (const { t, facet } of pq.terms) {
    if (facet || t.length < 4 || /^\d+$/.test(t)) continue;
    if (docs.some(d => d.all.includes(t))) continue;
    if (!vocab) { vocab = new Set(); for (const d of docs) for (const v of d.all.split(' ')) if (v && v !== '|') vocab.add(v); }
    alts.set(t, fuzzyAlts(t, vocab));
  }
  // a small recency nudge: equal matches → the newer work order first
  const nudge = (d) => d.recent ? 0.5 * Math.exp(-Math.max(0, tNow - d.recent) / (60 * 864e5)) : 0;
  let rows = [];
  for (const d of docs) { const s = scoreDoc(d, pq, alts, false); if (s) rows.push({ w: d.w, score: s.score + nudge(d), hits: s.hits }); }
  let loose = false;
  if (!rows.length && pq.terms.length > 1) {
    for (const d of docs) { const s = scoreDoc(d, pq, alts, true); if (s) rows.push({ w: d.w, score: s.score + nudge(d), hits: s.hits, loose: true }); }
    loose = rows.length > 0;
  }
  rows.sort((a, b) => b.score - a.score);
  const hlTerms = [...new Set([...pq.phrases, ...pq.terms.filter(x => !x.facet).map(x => x.t), ...[...alts.values()].flat()])];
  return { rows, loose, hlTerms, fuzzy: Object.fromEntries(alts) };
}

// Convenience for tests / one-off callers (the page caches docs itself).
export function search(wos, q, ctx, now) {
  const pq = parseQuery(q, ctx.today);
  return { pq, ...searchDocs(wos.map(w => buildDoc(w, ctx)), pq, now) };
}

// ── display ────────────────────────────────────────────────────────────
// Escape-safe highlight: match ranges are found on the RAW text and every
// segment is escaped on its own — never inject markup into escaped HTML.
// Words shorter than 3 letters only highlight at the start of a word.
export function highlight(raw, terms, esc, cls = 'mn-hl') {
  const s = String(raw == null ? '' : raw);
  const lower = s.toLowerCase();
  const rng = [];
  for (const t of terms || []) {
    if (!t || t.length < 2) continue;
    let i = 0;
    while ((i = lower.indexOf(t, i)) >= 0) {
      if (t.length >= 3 || i === 0 || !/[a-z0-9]/i.test(lower[i - 1])) rng.push([i, i + t.length]);
      i += t.length;
    }
  }
  if (!rng.length) return esc(s);
  rng.sort((a, b) => a[0] - b[0]);
  const merged = [];
  for (const r of rng) { const l = merged[merged.length - 1]; if (l && r[0] <= l[1]) l[1] = Math.max(l[1], r[1]); else merged.push([r[0], r[1]]); }
  let out = '', p = 0;
  for (const [a, b] of merged) { out += esc(s.slice(p, a)) + `<mark class="${cls}">` + esc(s.slice(a, b)) + '</mark>'; p = b; }
  return out + esc(s.slice(p));
}

// A short window of `raw` around the first matched word, highlighted.
export function snippet(raw, terms, esc, radius = 50, cls = 'mn-hl') {
  const s = String(raw == null ? '' : raw).replace(/\s+/g, ' ').trim();
  const lower = s.toLowerCase();
  let at = -1, len = 0;
  for (const t of terms || []) { if (!t || t.length < 2) continue; const i = lower.indexOf(t); if (i >= 0 && (at < 0 || i < at)) { at = i; len = t.length; } }
  if (at < 0) return highlight(s.length > radius * 2 ? `${s.slice(0, radius * 2)}…` : s, terms, esc, cls);
  let a = Math.max(0, at - radius), b = Math.min(s.length, at + len + radius);
  if (a > 0) { const sp = s.indexOf(' ', a); if (sp >= 0 && sp < at) a = sp + 1; }
  if (b < s.length) { const sp = s.lastIndexOf(' ', b); if (sp > at + len) b = sp; }
  return `${a > 0 ? '…' : ''}${highlight(s.slice(a, b), terms, esc, cls)}${b < s.length ? '…' : ''}`;
}
